import { useEffect, useMemo, useState } from 'react';
import Skeleton from '@/common/components/skeleton/Skeleton';
import useImageLoader from '@/common/hooks/useImage';
import useVideoLoader from '@/common/hooks/useVideo';
import { useLanguageData } from '@/common/hooks/useLanguageData';
import { CHAT_MEDIA_GALLERY_TYPE } from '../../utils/getConversationMediaFromMessages';
import {
	CompanionGalleryControls,
	CompanionGalleryDot,
	CompanionGalleryDots,
	CompanionGalleryGrid,
	CompanionGalleryNav,
	CompanionGalleryPage,
	CompanionGalleryViewport,
	CompanionMediaItem,
	CompanionMediaPlaceholder,
	CompanionMediaPlayBadge,
	CompanionMediaVideo,
} from './chatCompanion.styles';

const IMAGES_PER_PAGE = 4;

const ImagePlaceholderIcon = () => (
	<svg viewBox="0 0 24 24" aria-hidden="true">
		<rect x="4" y="5" width="16" height="14" rx="2" />
		<path d="m8 11 3 3 2-2 5 5" />
		<circle cx="9" cy="9" r="1.2" />
	</svg>
);

const GalleryPlaceholder = ({ label }) => (
	<CompanionMediaPlaceholder aria-hidden="true">
		<ImagePlaceholderIcon />
		<span>{label}</span>
	</CompanionMediaPlaceholder>
);

const ChevronLeftIcon = () => (
	<svg viewBox="0 0 24 24" aria-hidden="true">
		<path d="m14 7-5 5 5 5" />
	</svg>
);

const ChevronRightIcon = () => (
	<svg viewBox="0 0 24 24" aria-hidden="true">
		<path d="m10 7 5 5-5 5" />
	</svg>
);

const GalleryTile = ({ src, alt, onClick, placeholderLabel }) => {
	const { imageSrc, isLoading, hasError } = useImageLoader(src);

	if (isLoading) {
		return (
			<CompanionMediaItem type="button" disabled>
				<Skeleton width="100%" height="100%" radius="0" minWidth="100%" minHeight="100%" />
			</CompanionMediaItem>
		);
	}

	if (hasError) {
		return <GalleryPlaceholder label={placeholderLabel} />;
	}

	return (
		<CompanionMediaItem type="button" onClick={onClick}>
			<img src={imageSrc} alt={alt} />
		</CompanionMediaItem>
	);
};

const GalleryVideoTile = ({ src, alt, onClick, placeholderLabel }) => {
	const { videoSrc, isLoading, hasError } = useVideoLoader(src);

	if (isLoading) {
		return (
			<CompanionMediaItem type="button" disabled>
				<Skeleton width="100%" height="100%" radius="0" minWidth="100%" minHeight="100%" />
			</CompanionMediaItem>
		);
	}

	if (hasError || !videoSrc) {
		return <GalleryPlaceholder label={placeholderLabel} />;
	}

	return (
		<CompanionMediaItem type="button" onClick={onClick}>
			<CompanionMediaVideo src={videoSrc} muted playsInline preload="metadata" aria-hidden="true" />
			<CompanionMediaPlayBadge aria-hidden="true">▶</CompanionMediaPlayBadge>
		</CompanionMediaItem>
	);
};

const CompanionMediaGallery = ({ images, onOpenImage }) => {
	const t = useLanguageData('chat');
	const [page, setPage] = useState(0);

	const totalImages = images.length;
	const totalPages = Math.max(1, Math.ceil(totalImages / IMAGES_PER_PAGE));

	const pageSlots = useMemo(() => {
		const start = page * IMAGES_PER_PAGE;
		const slice = images.slice(start, start + IMAGES_PER_PAGE);

		return Array.from(
			{ length: IMAGES_PER_PAGE },
			(_, index) => slice[index] ?? null,
		);
	}, [images, page]);

	useEffect(() => {
		setPage((current) => Math.min(current, totalPages - 1));
	}, [totalPages, totalImages]);

	const canGoPrev = page > 0;
	const canGoNext = page < totalPages - 1;

	return (
		<>
			<CompanionGalleryViewport>
				<CompanionGalleryPage key={page}>
					<CompanionGalleryGrid>
						{pageSlots.map((image, index) => {
							const globalIndex = page * IMAGES_PER_PAGE + index;

							if (!image) {
								return (
									<GalleryPlaceholder
										key={`placeholder-${page}-${index}`}
										label={t.imagePlaceholder}
									/>
								);
							}

							if (image.type === CHAT_MEDIA_GALLERY_TYPE.VIDEO) {
								return (
									<GalleryVideoTile
										key={image.url || globalIndex}
										src={image.url}
										alt={image.caption || t.generatedVideoAlt}
										placeholderLabel={t.imagePlaceholder}
										onClick={() => onOpenImage(globalIndex)}
									/>
								);
							}

							return (
								<GalleryTile
									key={image.url || globalIndex}
									src={image.url}
									alt={image.caption}
									placeholderLabel={t.imagePlaceholder}
									onClick={() => onOpenImage(globalIndex)}
								/>
							);
						})}
					</CompanionGalleryGrid>
				</CompanionGalleryPage>
			</CompanionGalleryViewport>

			{totalPages > 1 && (
				<CompanionGalleryControls>
					<CompanionGalleryNav
						type="button"
						onClick={() => setPage((current) => current - 1)}
						disabled={!canGoPrev}
						aria-label={t.galleryPrev}
					>
						<ChevronLeftIcon />
					</CompanionGalleryNav>

					<CompanionGalleryDots>
						{Array.from({ length: totalPages }).map((_, index) => (
							<CompanionGalleryDot
								key={index}
								type="button"
								$active={index === page}
								onClick={() => setPage(index)}
								aria-label={`${index + 1}/${totalPages}`}
							/>
						))}
					</CompanionGalleryDots>

					<CompanionGalleryNav
						type="button"
						onClick={() => setPage((current) => current + 1)}
						disabled={!canGoNext}
						aria-label={t.galleryNext}
					>
						<ChevronRightIcon />
					</CompanionGalleryNav>
				</CompanionGalleryControls>
			)}
		</>
	);
};

export default CompanionMediaGallery;
